import React from 'react';
import { Container, Typography, Paper, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

export default function FullIntegration2Docs() {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Full Integration - Part 2
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/')}>
          🏠 HOME
        </Button>
      </Box>

      <Typography variant="body1" sx={{ mb: 3 }}>
        실제 컴포넌트를 구현하여 React Hook Form, MUI, GraphQL이 어떻게 함께 작동하는지 살펴보겠습니다. 
        상품 관리부터 실시간 업데이트까지, 완전한 기능을 가진 컴포넌트들을 만들어보겠습니다.
      </Typography>

      <Typography variant="h4" gutterBottom sx={{ mt: 4, mb: 2 }}>
        실제 컴포넌트 구현
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        통합 상품 관리 컴포넌트
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        탭을 사용하여 상품 등록과 목록을 한 화면에서 관리할 수 있는 통합 컴포넌트입니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`import { useState } from 'react';
import { Box, Tabs, Tab, Paper } from '@mui/material';
import ProductForm from './ProductForm';
import ProductTable from './ProductTable';

function ProductManagement() {
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="상품 등록" />
          <Tab label="상품 목록" />
        </Tabs>
      </Box>
      
      <Box sx={{ mt: 3 }}>
        {currentTab === 0 && <ProductForm />}
        {currentTab === 1 && <ProductTable />}
      </Box>
    </Paper>
  );
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        상품 등록 폼 (React Hook Form + MUI)
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Controller를 사용하여 MUI 컴포넌트를 React Hook Form과 연결하고, GraphQL mutation으로 데이터를 전송합니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@apollo/client';
import { 
  TextField, Select, MenuItem, Button, 
  Grid, FormControl, InputLabel,
  Alert, CircularProgress
} from '@mui/material';

const CREATE_PRODUCT = gql\`
  mutation CreateProduct($input: ProductInput!) {
    createProduct(input: $input) {
      id
      name
      price
      category {
        id
        name
      }
    }
  }
\`;

function ProductForm() {
  const { data: categoriesData } = useQuery(GET_CATEGORIES);
  const [createProduct, { loading, error }] = useMutation(CREATE_PRODUCT);

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: yupResolver(productSchema),
    defaultValues: {
      name: '',
      description: '',
      price: 0,
      categoryId: ''
    }
  });

  const onSubmit = async (data) => {
    try {
      await createProduct({
        variables: { input: data },
        refetchQueries: ['GetProducts']
      });
      reset();
      toast.success('상품이 등록되었습니다!');
    } catch (err) {
      console.error('등록 실패:', err);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="상품명"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Controller
            name="categoryId"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.categoryId}>
                <InputLabel>카테고리</InputLabel>
                <Select {...field} label="카테고리">
                  {categoriesData?.categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="상품 설명"
                multiline
                rows={3}
                fullWidth
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error.message}
            </Alert>
          )}
          
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? '등록 중...' : '상품 등록'}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        상품 테이블 (MUI DataGrid + GraphQL)
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        DataGrid를 사용하여 상품 목록을 표시하고, 편집/삭제 기능을 제공합니다. 실시간 데이터 업데이트도 지원합니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Edit, Delete, Visibility } from '@mui/icons-material';
import { Chip, Avatar } from '@mui/material';

function ProductTable() {
  const { data, loading, refetch } = useQuery(GET_PRODUCTS);
  const [deleteProduct] = useMutation(DELETE_PRODUCT);

  const handleEdit = (id) => {
    // 편집 로직
  };

  const handleDelete = async (id) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      await deleteProduct({ 
        variables: { id },
        refetchQueries: ['GetProducts']
      });
    }
  };

  const columns = [
    {
      field: 'image',
      headerName: '이미지',
      width: 80,
      renderCell: (params) => (
        <Avatar
          src={params.row.images?.[0]}
          alt={params.row.name}
          variant="square"
        />
      ),
    },
    { 
      field: 'name', 
      headerName: '상품명', 
      flex: 1,
      minWidth: 150
    },
    { 
      field: 'price', 
      headerName: '가격', 
      type: 'number',
      width: 120,
      renderCell: (params) => \`₩\${params.value.toLocaleString()}\`
    },
    { 
      field: 'category', 
      headerName: '카테고리',
      width: 120,
      renderCell: (params) => (
        <Chip 
          label={params.row.category.name} 
          size="small" 
          color="primary"
        />
      )
    },
    {
      field: 'inventory',
      headerName: '재고',
      width: 100,
      renderCell: (params) => {
        const qty = params.row.inventory.quantity;
        const minStock = params.row.inventory.minStock;
        
        return (
          <Chip
            label={qty}
            size="small"
            color={qty <= minStock ? 'error' : 'success'}
          />
        );
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '작업',
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<Visibility />}
          label="보기"
          onClick={() => handleView(params.id)}
        />,
        <GridActionsCellItem
          icon={<Edit />}
          label="수정"
          onClick={() => handleEdit(params.id)}
        />,
        <GridActionsCellItem
          icon={<Delete />}
          label="삭제"
          onClick={() => handleDelete(params.id)}
        />
      ]
    }
  ];

  return (
    <Box sx={{ height: 500 }}>
      <DataGrid
        rows={data?.products || []}
        columns={columns}
        loading={loading}
        checkboxSelection
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50]}
        sx={{
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      />
    </Box>
  );
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        실시간 재고 모니터링
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        GraphQL Subscription을 사용하여 재고 변동사항을 실시간으로 감지하고 알림을 표시합니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`import { useSubscription } from '@apollo/client';
import { Alert, Box, Typography } from '@mui/material';

const INVENTORY_SUBSCRIPTION = gql\`
  subscription {
    inventoryUpdated {
      id
      productId
      quantity
      minStock
      product {
        name
      }
    }
  }
\`;

function InventoryMonitor() {
  const [alerts, setAlerts] = useState([]);
  const { data } = useSubscription(INVENTORY_SUBSCRIPTION);

  useEffect(() => {
    if (data?.inventoryUpdated) {
      const { quantity, minStock, product } = data.inventoryUpdated;
      
      if (quantity <= minStock) {
        const newAlert = {
          id: Date.now(),
          message: \`\${product.name} 재고가 부족합니다! (현재: \${quantity}개)\`,
          severity: 'warning',
          timestamp: new Date()
        };
        
        setAlerts(prev => [newAlert, ...prev.slice(0, 4)]);
        
        // 5초 후 알림 제거
        setTimeout(() => {
          setAlerts(prev => prev.filter(alert => alert.id !== newAlert.id));
        }, 5000);
      }
    }
  }, [data]);

  return (
    <Box sx={{ position: 'fixed', top: 80, right: 20, zIndex: 1000 }}>
      {alerts.map(alert => (
        <Alert 
          key={alert.id} 
          severity={alert.severity}
          sx={{ mb: 1, minWidth: 300 }}
        >
          {alert.message}
        </Alert>
      ))}
    </Box>
  );
}`}
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/docs/full-integration-1')}
        >
          ← PART 1: 프로젝트 구조
        </Button>
        <Button 
          variant="contained" 
          onClick={() => navigate('/docs/full-integration-3')}
        >
          PART 3: 대시보드와 고급 기능 →
        </Button>
      </Box>
    </Container>
  );
}
